// Configurações da API para conectar com o backend
// Deploy unificado: frontend e backend no mesmo servidor, usar URL relativa
const API_BASE_URL = "/api";

// Filtra campos null/undefined preservando booleanos, strings vazias e números
const filterDefinedFields = (data) =>
  Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;

    // Timeout padrão para requisições (em ms)
    this.defaultTimeout = 10000;
  }

  // Método genérico para fazer requisições
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Controller para permitir cancelamento por timeout
    const controller = new AbortController();
    const timeoutMs =
      typeof options.timeout === "number"
        ? options.timeout
        : this.defaultTimeout;
    const timeoutId = setTimeout(
      () => {
        // Abortar requisição se exceder tempo limite
        try {
          controller.abort();
        } catch (_) {
          // Ignorar erros de abort
        }
      },
      Math.max(1000, timeoutMs),
    );

    // Cache seletivo: não forçar no-cache por padrão
    // Permitir que endpoints específicos definam suas próprias políticas
    const defaultHeaders = {
      "Content-Type": "application/json",
    };

    // Adicionar headers de no-cache apenas se explicitamente solicitado
    if (options.noCache === true) {
      defaultHeaders["Cache-Control"] = "no-cache, no-store, must-revalidate";
      defaultHeaders["Pragma"] = "no-cache";
      defaultHeaders["Expires"] = "0";
    }

    const config = {
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal: controller.signal,
      ...options,
    };

    // Adicionar token de autorização se existir
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);

      // Tratar respostas não OK enriquecendo o erro para o frontend
      if (!response.ok) {
        let errorBody = null;
        try {
          const ct = response.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            errorBody = await response.json();
          } else {
            const txt = await response.text();
            try {
              errorBody = JSON.parse(txt);
            } catch (_) {
              errorBody = { message: txt };
            }
          }
        } catch (_) {
          // Ignorar erros ao ler corpo
        }

        const errorMessage = errorBody?.error?.message || errorBody?.message || response.statusText || "Erro na requisição";
        const err = new Error(`HTTP ${response.status}: ${errorMessage}`);
        // Compatibilidade com padrão axios-like usado em partes do frontend
        err.status = response.status;
        err.data = errorBody;
        err.response = { status: response.status, data: errorBody };
        err.url = url;
        throw err;
      }

      // Validar se a resposta é JSON
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        return await response.json();
      }

      // Tentar fazer parse mesmo sem content-type correto
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (_) {
        throw new Error("Resposta da API não é um JSON válido.");
      }
    } catch (error) {
      // Diferenciar timeout/abort
      if (error?.name === "AbortError") {
        throw new Error(
          "Tempo de resposta excedido. Tente novamente mais tarde.",
        );
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Métodos genéricos GET e POST
  async get(endpoint) {
    return this.request(endpoint, {
      method: "GET",
    });
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Métodos de autenticação
  async login(credentials) {
    return this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });
  }

  async logout() {
    return this.request("/auth/logout", {
      method: "POST",
    });
  }

  async getCurrentUser() {
    return this.request("/auth/me");
  }

  // Métodos para usuários
  async getUser(id) {
    return this.request(`/users/${id}`);
  }

  async getUsers(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== "" &&
        params[key] !== null &&
        params[key] !== undefined
      ) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return this.request(`/users${queryString ? "?" + queryString : ""}`, {
      method: "GET",
      noCache: true,
    });
  }

  async createUser(userData) {
    // Remover campos desnecessários
    const {
      phone,
      department,
      redis_host,
      redis_port,
      redis_password,
      ...cleanUserData
    } = userData;

    const finalData = filterDefinedFields(cleanUserData);

    return this.request("/users", {
      method: "POST",
      body: JSON.stringify(finalData),
    });
  }

  async updateUser(id, userData) {
    // Remover campos desnecessários
    const {
      phone,
      department,
      redis_host,
      redis_port,
      redis_password,
      ...cleanUserData
    } = userData;

    // Converter active para boolean se necessário
    if (cleanUserData.active !== undefined) {
      cleanUserData.active = Boolean(cleanUserData.active);
    }

    const finalData = filterDefinedFields(cleanUserData);

    return this.request(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(finalData),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: "DELETE",
    });
  }

  async checkTables(id) {
    return this.request(`/users/${id}/check-tables`, {
      method: "GET",
      noCache: true
    });
  }

  async fixTables(id) {
    return this.request(`/users/${id}/fix-tables`, {
      method: "POST"
    });
  }

  async toggleUserStatus(id) {
    return this.request(`/users/${id}/toggle-status`, {
      method: "PUT",
    });
  }

  async getUserStats() {
    return this.request("/users/stats/overview");
  }

  // Métodos para dashboard
  async getDashboardOverview() {
    return this.request("/dashboard/overview");
  }

  async getDashboardCharts(type, period = "7d") {
    return this.request(`/dashboard/charts/${type}?period=${period}`);
  }

  async getDashboardInteractions(month) {
    const url = month
      ? `/dashboard/charts/interactions?month=${month}`
      : `/dashboard/charts/interactions`;
    return this.request(url);
  }

  async getDashboardActivities(page = 1, limit = 20) {
    return this.request(`/dashboard/activities?page=${page}&limit=${limit}`);
  }

  async getSystemHealth() {
    return this.request("/dashboard/system/health");
  }

  // Métodos para CRM
  async getCRMClients(page = 1, limit = 10) {
    return this.request(`/crm/clients?page=${page}&limit=${limit}`);
  }

  async getCRMClient(id) {
    return this.request(`/crm/clients/${id}`);
  }

  async createCRMClient(clientData) {
    return this.request("/crm/clients", {
      method: "POST",
      body: JSON.stringify(clientData),
    });
  }

  async updateCRMClient(id, clientData) {
    return this.request(`/crm/clients/${id}`, {
      method: "PUT",
      body: JSON.stringify(clientData),
    });
  }

  async deleteCRMClient(id) {
    return this.request(`/crm/clients/${id}`, {
      method: "DELETE",
    });
  }

  async getCRMStats() {
    return this.request("/crm/stats");
  }

  // Métodos para WhatsApp
  async getWhatsAppConversations(page = 1, limit = 10) {
    return this.request(`/whatsapp/conversations?page=${page}&limit=${limit}`);
  }

  async getWhatsAppConversation(id) {
    return this.request(`/whatsapp/conversations/${id}`);
  }

  async getWhatsAppMessages(conversationId) {
    return this.request(`/whatsapp/conversations/${conversationId}/messages`);
  }

  async sendWhatsAppMessage(conversationId, messageData) {
    return this.request(`/whatsapp/conversations/${conversationId}/messages`, {
      method: "POST",
      body: JSON.stringify(messageData),
    });
  }

  async getWhatsAppStats() {
    return this.request("/whatsapp/stats");
  }

  async getWhatsAppContacts(page = 1, limit = 50, search = "") {
    const params = new URLSearchParams();
    if (page) params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    if (search) params.append("search", search);

    return this.request(`/whatsapp/contacts?${params.toString()}`);
  }

  // Métodos para envio de mensagens WhatsApp
  async sendWhatsAppMessageToContact(messageData) {
    return this.request("/whatsapp/send-message", {
      method: "POST",
      body: JSON.stringify(messageData),
    });
  }

  async sendWhatsAppBulkMessages(campaignData) {
    return this.request("/whatsapp/send-bulk", {
      method: "POST",
      body: JSON.stringify(campaignData),
    });
  }

  // Métodos para IA
  async getIASettings() {
    return this.request("/ia/settings");
  }

  async updateIASetting(key, value) {
    return this.request(`/ia/settings/${key}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    });
  }

  async chatWithIA(message) {
    return this.request("/ia/chat", {
      method: "POST",
      body: JSON.stringify({ message }),
    });
  }

  async getIAStats() {
    return this.request("/ia/stats");
  }

  async getIAModels() {
    return this.request("/ia/models");
  }

  // Métodos para Documentos
  async uploadDocument(file, targetDbName) {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("target_db_name", targetDbName);

    const token = localStorage.getItem("token");
    return fetch(`${this.baseURL}/documents/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // Não definir Content-Type para permitir multipart/form-data
      },
      body: formData,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  }

  async getDocuments(targetDbName, options = {}) {
    const { limit = 100, search = "" } = options;
    const params = new URLSearchParams();
    if (limit) params.append("limit", limit);
    if (search) params.append("search", search);

    const queryString = params.toString();
    return this.request(
      `/documents/list/${targetDbName}${queryString ? "?" + queryString : ""}`,
    );
  }

  async deleteDocument(targetDbName, documentId) {
    return this.request(`/documents/${targetDbName}/${documentId}`, {
      method: "DELETE",
    });
  }

  async clearAllDocuments(targetDbName) {
    return this.request(`/documents/clear/${targetDbName}`, {
      method: "DELETE",
    });
  }

  async searchDocuments(targetDbName, query, limit = 5) {
    return this.request(`/documents/search/${targetDbName}`, {
      method: "POST",
      body: JSON.stringify({ query, limit }),
    });
  }

  async getDocumentStats(targetDbName) {
    return this.request(`/documents/stats/${targetDbName}`);
  }

  // Métodos de autenticação adicionais
  async refreshToken() {
    return this.request("/auth/refresh", {
      method: "POST",
    });
  }

  async changePassword(passwordData) {
    return this.request("/auth/change-password", {
      method: "PUT",
      body: JSON.stringify(passwordData),
    });
  }

  async logoutAll() {
    return this.request("/auth/logout-all", {
      method: "POST",
    });
  }

  // Métodos para contatos
  async getContatos(targetDbName) {
    return this.request(`/contatos/list/${targetDbName}`);
  }

  async getContatosStats(targetDbName) {
    return this.request(`/contatos/stats/${targetDbName}`);
  }

  // Método para importar contatos do sistema para o WhatsApp
  async importContactsToWhatsApp(targetDbName) {
    return this.request(`/whatsapp/import-contacts/${targetDbName}`, {
      method: "POST",
    });
  }

  // Método para importar contatos de arquivo CSV ou DOCX
  async importContactsFromFile(file, targetDbName) {
    const formData = new FormData();
    formData.append("file", file);

    return this.request(`/whatsapp/import-contacts-file/${targetDbName}`, {
      method: "POST",
      body: formData,
      // Não definir Content-Type para FormData - o browser define automaticamente
      headers: {
        // Remove Content-Type para permitir que o browser defina com boundary
      },
    });
  }

  // Método para limpar todos os contatos
  async clearAllContacts(targetDbName) {
    return this.request("/whatsapp/clear-all-contacts", {
      method: "DELETE",
      body: JSON.stringify({ targetDbName }),
    });
  }

  // Métodos para campanhas WhatsApp
  async getCampaigns() {
    return this.request("/whatsapp/campaigns");
  }

  async getCampaign(campaignId) {
    return this.request(`/whatsapp/campaigns/${campaignId}`);
  }

  async getCampaignStatus(campaignId) {
    return this.request(`/whatsapp/campaigns/${campaignId}/status`);
  }

  async toggleCampaign(campaignId, action) {
    return this.request(`/whatsapp/campaigns/${campaignId}/toggle`, {
      method: "POST",
      body: JSON.stringify({ action }),
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async stopCampaign(campaignId) {
    return this.request(`/whatsapp/campaigns/${campaignId}/stop`, {
      method: "POST",
    });
  }

  async getCampaignStats() {
    return this.request("/whatsapp/campaigns/stats");
  }

  async getActiveCampaign() {
    return this.request("/whatsapp/campaigns/active");
  }

  async getCampaignMessages(campaignId, page = 1, limit = 50) {
    return this.request(
      `/whatsapp/campaigns/${campaignId}/messages?page=${page}&limit=${limit}`,
    );
  }

  // Métodos de Analytics
  async getInteractions(targetDbName, params) {
    let url = `/analytics/interactions/${targetDbName}`;
    const queryParams = new URLSearchParams();

    if (typeof params === 'string') {
      queryParams.append('month', params);
    } else if (typeof params === 'object' && params !== null) {
      if (params.month) queryParams.append('month', params.month);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
    }

    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;

    return this.request(url);
  }

  async getTotalInteractions() {
    return this.request(`/analytics/total-interactions`);
  }

  async getResponseTime(targetDbName, params) {
    let url = `/analytics/response-time/${targetDbName}`;
    const queryParams = new URLSearchParams();

    if (typeof params === 'string') {
      queryParams.append('month', params);
    } else if (typeof params === 'object' && params !== null) {
      if (params.month) queryParams.append('month', params.month);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
    }

    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;

    return this.request(url);
  }

  async getHumanInterventions(targetDbName, params) {
    let url = `/analytics/human-interventions/${targetDbName}`;
    const queryParams = new URLSearchParams();

    if (typeof params === 'string') {
      queryParams.append('month', params);
    } else if (typeof params === 'object' && params !== null) {
      if (params.month) queryParams.append('month', params.month);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
    }

    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;

    return this.request(url);
  }

  async getTotalHumanInterventions() {
    return this.request(`/analytics/total-human-interventions`);
  }

  // Métodos para snapshots e coleta de dados
  async getSnapshots(targetDbName) {
    return this.request(`/analytics/snapshots/${targetDbName}`);
  }

  async getChartData(targetDbName, params) {
    let url = `/analytics/chart-data/${targetDbName}`;
    const queryParams = new URLSearchParams();

    if (typeof params === 'string') {
      queryParams.append('month', params);
    } else if (typeof params === 'object' && params !== null) {
      if (params.month) queryParams.append('month', params.month);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
    }

    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;

    return this.request(url);
  }

  async collectData() {
    return this.request("/analytics/collect", {
      method: "POST",
    });
  }

  async cleanupData() {
    return this.request("/analytics/cleanup", {
      method: "POST",
    });
  }

  async getSchedulerStatus() {
    return this.request("/analytics/scheduler/status");
  }

  async triggerSnapshot() {
    return this.request("/analytics/trigger-snapshot", {
      method: "POST",
    });
  }

  // Métodos para solicitações de intervenção humana
  async getNotifications(page = 1, limit = 10, pendingOnly = false) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      pending_only: pendingOnly.toString(),
      _t: Date.now().toString(), // Timestamp para evitar cache
    });

    return this.request(`/notifications?${params.toString()}`);
  }

  async markNotificationAsRead(requestId) {
    return this.request(`/notifications/${requestId}/acknowledge`, {
      method: "PUT",
    });
  }

  async markAllNotificationsAsRead() {
    return this.request("/notifications/acknowledge-all", {
      method: "PUT",
      noCache: true,
    });
  }

  async getUnreadNotificationsCount() {
    const response = await this.getNotifications(1, 1, true);
    return response.pending_count || 0;
  }

  // Métodos para Evolution API - WhatsApp Connection
  async createEvolutionInstance(instanceData) {
    return this.request("/evolution/instance/create", {
      method: "POST",
      body: JSON.stringify(instanceData),
    });
  }

  async getEvolutionInstanceStatus(instanceName) {
    return this.request(`/evolution/instance/status/${instanceName}`);
  }

  async getEvolutionQRCode(instanceName) {
    return this.request(`/evolution/instance/qrcode/${instanceName}`);
  }

  async deleteEvolutionInstance(instanceName) {
    return this.request(`/evolution/instance/delete/${instanceName}`, {
      method: "DELETE",
    });
  }

  async testEvolutionConnection() {
    return this.request("/evolution/test-connection");
  }

  // Métodos para gerenciamento de tabelas WhatsApp
  async createWppTablesForUser(userId) {
    return this.request(`/users/${userId}/create-wpp-tables`, {
      method: "POST",
    });
  }

  async checkWppTablesForUser(userId) {
    return this.request(`/users/${userId}/check-wpp-tables`);
  }

  // Métodos para debug de respostas WhatsApp

  async getRecentMessages(limit = 10) {
    return this.request(`/evolution/debug/recent-messages?limit=${limit}`);
  }

  // Método para upload de imagem WhatsApp
  async uploadWhatsAppImage(imageFile) {
    const formData = new FormData();
    formData.append("image", imageFile);

    const token = localStorage.getItem("token");
    return fetch(`${this.baseURL}/whatsapp/upload-image`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // Não definir Content-Type para permitir multipart/form-data
      },
      body: formData,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  }
}

// Instância singleton da API
const apiService = new ApiService();

export default apiService;

// Interceptor para lidar com erros de autenticação
apiService.interceptResponse = (response) => {
  if (response.status === 401) {
    // Token expirado ou inválido
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  }
  return response;
};

// Funções auxiliares para gerenciar token
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
};

export const getAuthToken = () => {
  return localStorage.getItem("token");
};

// Função para verificar se o usuário está autenticado
export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

